import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const GHOST_ID = 13;

export default function FinancePage() {
  const [reservations, setReservations] = useState([]);
  const [spots, setSpots] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [VAT, setVAT] = useState(0.22);
  const [includeUnassigned, setIncludeUnassigned] = useState(true);


  // Main year used by the existing tables/charts
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  // Compare block: two years side-by-side
  const [yearA, setYearA] = useState(dayjs().year());
  const [yearB, setYearB] = useState(dayjs().year() - 1);

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  // Re-fetch everything
  async function fetchData() {
    setLoading(true);

    // NOTE: include "apartment_id" in reservations for the new table
    const { data: resData } = await supabase
      .from("reservations")
      .select(
        "start_date, end_date, spot_id, apartment_id, total_price, manual_override, booking_type"
      )
      .neq("spot_id", GHOST_ID);

    const { data: spotData } = await supabase
      .from("spots")
      .select("id, type")
      .neq("id", GHOST_ID)
      .order("id", { ascending: true });

    const { data: priceData } = await supabase
      .from("pricing")
      .select("month, type, price_per_day");

    const { data: aptData } = await supabase
      .from("apartments")
      .select("id, name, active")
      .order("name", { ascending: true });

    setReservations(resData || []);
    setSpots(spotData || []);
    setPricing(priceData || []);
    setApartments(aptData || []);
    setLoading(false);
  }

  // --- Optional realtime auto-refresh (enable in Supabase: Database > Replication > Realtime)
  useEffect(() => {
    const channel = supabase
      .channel("finance_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "reservations" },
        () => fetchData()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "apartments" },
        () => fetchData()
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
  }

  function getPriceForMonth(month, type) {
    const row = pricing.find((p) => p.month === month && p.type === type);
    return row ? row.price_per_day : 0;
  }

  const currentYear = selectedYear;
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // ---------- Existing monthly turnover + occupancy ----------
  const monthlyData = months.map((m) => {
    let turnover = 0;
    let bookedDays = 0;
    let bookedDaysShort = 0;
    let bookedDaysLong = 0;
    let turnoverShort = 0;
    let turnoverLong = 0;

    reservations.forEach((r) => {
      const start = dayjs(r.start_date);
      const end = dayjs(r.end_date);
      const totalDays = end.diff(start, "day") + 1;
      let current = start;

      while (current.isSame(end) || current.isBefore(end)) {
        const year = current.year();
        const month = current.month() + 1;

        if (year === selectedYear && month === m) {
          let priceForDay = 0;
          if (r.manual_override) {
            priceForDay = Number(r.total_price) / totalDays;
          } else {
            const spot = spots.find((s) => s.id === r.spot_id);
            const spotType = spot?.type || "normal";
            priceForDay = getPriceForMonth(month, spotType);
          }

          turnover += priceForDay;
          bookedDays++;

          if (r.booking_type === "short-term") {
            turnoverShort += priceForDay;
            bookedDaysShort++;
          } else if (r.booking_type === "long-term") {
            turnoverLong += priceForDay;
            bookedDaysLong++;
          }
        }

        current = current.add(1, "day");
      }
    });

    const totalDaysInMonth = spots.length * daysInMonth(currentYear, m);
    const occupancy = totalDaysInMonth > 0 ? (bookedDays / totalDaysInMonth) * 100 : 0;
    const occupancyShort =
      totalDaysInMonth > 0 ? (bookedDaysShort / totalDaysInMonth) * 100 : 0;
    const occupancyLong =
      totalDaysInMonth > 0 ? (bookedDaysLong / totalDaysInMonth) * 100 : 0;

    return {
      month: m,
      turnoverInclVAT: turnover,
      turnoverShortInclVAT: turnoverShort,
      turnoverLongInclVAT: turnoverLong,
      turnoverExclVAT: turnover / (1 + VAT),
      turnoverShortExclVAT: turnoverShort / (1 + VAT),
      turnoverLongExclVAT: turnoverLong / (1 + VAT),
      occupancy,
      occupancyShort,
      occupancyLong,
    };
  });

  const totalTurnoverInclVAT = monthlyData.reduce(
    (sum, m) => sum + m.turnoverInclVAT,
    0
  );
  const totalTurnoverExclVAT = monthlyData.reduce(
    (sum, m) => sum + m.turnoverExclVAT,
    0
  );
  const totalShortInclVAT = monthlyData.reduce(
    (sum, m) => sum + m.turnoverShortInclVAT,
    0
  );
  const totalLongInclVAT = monthlyData.reduce(
    (sum, m) => sum + m.turnoverLongInclVAT,
    0
  );

  // ---------- NEW: Bookings per apartment (year compare) ----------

  function overlapsYear(r, year) {
    const s = dayjs(r.start_date);
    const e = dayjs(r.end_date);
    const yStart = dayjs(`${year}-01-01`);
    const yEnd = dayjs(`${year}-12-31`);
    // overlap if not entirely before or after the year
    return !(e.isBefore(yStart, "day") || s.isAfter(yEnd, "day"));
  }

  const activeApartments = useMemo(
    () => (apartments || []).filter((a) => a.active),
    [apartments]
  );

  function buildApartmentStatsForYear(year) {
  const counts = new Map(activeApartments.map((a) => [a.id, 0]));
  let unassigned = 0;

  reservations.forEach((r) => {
    if (!overlapsYear(r, year)) return;
    const aid = r.apartment_id ?? null;
    if (!aid) {
      unassigned++;
      return;
    }
    if (counts.has(aid)) counts.set(aid, counts.get(aid) + 1);
    else counts.set(aid, 1); // inactive/missing apt still counted
  });

  const rows = [];
  let total = 0;

  // active apartments first
  activeApartments.forEach((a) => {
    const n = counts.get(a.id) || 0;
    total += n;
    rows.push({ id: a.id, name: a.name, bookings: n });
  });

  // apartments that had bookings but are not active
  counts.forEach((n, id) => {
    if (!activeApartments.find((a) => a.id === id)) {
      total += n;
      rows.push({ id, name: `Apartment #${id}`, bookings: n });
    }
  });

  // only add Unassigned if toggle is ON
  if (includeUnassigned && unassigned > 0) {
    total += unassigned;
    rows.push({ id: "unassigned", name: "Unassigned", bookings: unassigned });
  }

  rows.sort((a, b) => b.bookings - a.bookings);

  return {
    total,
    unassigned, // handy to show what's being hidden
    rows: rows.map((r) => ({
      ...r,
      percent: total > 0 ? (r.bookings / total) * 100 : 0,
    })),
  };
}

  const statsA = useMemo(() => buildApartmentStatsForYear(yearA), [reservations, apartments, yearA, includeUnassigned]);
  const statsB = useMemo(() => buildApartmentStatsForYear(yearB), [reservations, apartments, yearB, includeUnassigned]);

  

  const yearsAvailable = useMemo(() => {
    // Union of all years touched by reservations (start or end)
    const ys = new Set();
    reservations.forEach((r) => {
      ys.add(dayjs(r.start_date).year());
      ys.add(dayjs(r.end_date).year());
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [reservations]);

  if (loading) return <div style={{ padding: 20 }}>Loading...</div>;

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  return (
    <div style={{ padding: 20 }}>
      <h1>Finance Overview ({selectedYear})</h1>

      <div style={{ display: "flex", flexDirection: "column", gap: "30px" }}>
        {/* Controls + Table + Explanation */}
        <div
          style={{
            display: "flex",
            flexDirection: window.innerWidth < 768 ? "column" : "row",
            alignItems: "flex-start",
            gap: "40px",
          }}
        >
          {/* Table + Controls */}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: 20 }}>
              <label>
                Year:{" "}
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {yearsAvailable.length === 0
                    ? [selectedYear].map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))
                    : yearsAvailable.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                </select>
              </label>

              <label>
                VAT %:{" "}
                <input
                  type="number"
                  step="0.01"
                  value={(VAT * 100).toFixed(2)}
                  onChange={(e) => setVAT(Number(e.target.value) / 100)}
                  style={{ width: "80px", marginLeft: "10px" }}
                />
              </label>

              <button onClick={fetchData} style={{ marginLeft: "auto" }}>
                Refresh
              </button>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ minWidth: "600px" }} border="1" cellPadding="10">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Turnover (excl. VAT)</th>
                    <th>Turnover (incl. VAT)</th>
                    <th>Occupancy %</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((row) => (
                    <tr key={row.month}>
                      <td>{monthNames[row.month - 1]}</td>
                      <td>€{row.turnoverExclVAT.toFixed(2)}</td>
                      <td>€{row.turnoverInclVAT.toFixed(2)}</td>
                      <td>{row.occupancy.toFixed(1)}%</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold", backgroundColor: "#f4f4f4" }}>
                    <td>Total</td>
                    <td>€{totalTurnoverExclVAT.toFixed(2)}</td>
                    <td>€{totalTurnoverInclVAT.toFixed(2)}</td>
                    <td>-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Explanation */}
          <div style={{ maxWidth: "300px", fontSize: "14px", color: "#555" }}>
            <strong>How this is calculated:</strong>
            <br />
            • Bookings with <em>manual prices</em> are evenly split across their days.
            <br />
            • Normal bookings use the pricing table by spot type and month.
            <br />
            • Occupancy % is based on booked days vs. total available spot-days per month.
          </div>
        </div>


        {/* Turnover Chart */}
        <div>
          <h2>Turnover per Month</h2>
          <div style={{ width: "100%", minHeight: 300 }}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={monthlyData}>
                <XAxis dataKey="month" tickFormatter={(m) => monthNames[m - 1]} />
                <YAxis />
                <Tooltip />
                <Legend />
                  <Bar dataKey="turnoverShortExclVAT" name="Short-term excl. VAT" fill="#4E79A7" />
                  <Bar dataKey="turnoverShortInclVAT" name="Short-term incl. VAT" fill="#A0CBE8" />
                  <Bar dataKey="turnoverLongExclVAT"  name="Long-term excl. VAT"  fill="#F28E2B" />
                  <Bar dataKey="turnoverLongInclVAT"  name="Long-term incl. VAT"  fill="#FFBE7D" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Totals */}
        <div style={{ fontSize: "16px" }}>
          <h3>Total Revenue Summary (incl. VAT)</h3>
          <p><strong>Total Revenue:</strong> €{totalTurnoverInclVAT.toFixed(2)}</p>
          <p>– Short-term bookings: €{totalShortInclVAT.toFixed(2)}</p>
          <p>– Long-term bookings: €{totalLongInclVAT.toFixed(2)}</p>
        </div>

        {/* Occupancy Chart */}
        <div>
          <h2>Occupancy % per Month</h2>
          <div style={{ width: "100%", height: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={(m) => monthNames[m - 1]} />
                <YAxis unit="%" />
                <Tooltip />
                <Legend />
                    <Bar dataKey="occupancy"      name="Total Occupancy %"      fill="#59A14F" />
                    <Bar dataKey="occupancyShort" name="Short-term Occupancy %" fill="#EDC948" />
                    <Bar dataKey="occupancyLong"  name="Long-term Occupancy %"  fill="#E15759" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* NEW: Bookings by Apartment (Year Compare) */}
        <div>
          <h2>Bookings per Apartment (Year Compare)</h2>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
  <label>
    Year A:{" "}
    <select value={yearA} onChange={(e) => setYearA(Number(e.target.value))}>
      {(yearsAvailable.length ? yearsAvailable : [yearA]).map((y) => (
        <option key={`ya-${y}`} value={y}>{y}</option>
      ))}
    </select>
  </label>

  <label>
    Year B:{" "}
    <select value={yearB} onChange={(e) => setYearB(Number(e.target.value))}>
      {(yearsAvailable.length ? yearsAvailable : [yearB]).map((y) => (
        <option key={`yb-${y}`} value={y}>{y}</option>
      ))}
    </select>
  </label>

  <button onClick={() => { setYearA(yearB); setYearB(yearA); }}>
    Swap A↔B
  </button>

  <label style={{ marginLeft: 8 }}>
    <input
      type="checkbox"
      checked={includeUnassigned}
      onChange={(e) => setIncludeUnassigned(e.target.checked)}
      style={{ marginRight: 6 }}
    />
    Include “Unassigned”
  </label>
</div>


          <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: 700 }} border="1" cellPadding="8">
              <thead>
                <tr style={{ background: "#f4f4f4" }}>
                  <th style={{ textAlign: "left" }}>Apartment</th>
                  <th colSpan={2} style={{ textAlign: "center" }}>{yearA}</th>
                  <th colSpan={2} style={{ textAlign: "center" }}>{yearB}</th>
                </tr>
                <tr>
                  <th />
                  <th>nr bookings</th>
                  <th>%</th>
                  <th>nr bookings</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...new Set([
                    ...statsA.rows.map((r) => r.id),
                    ...statsB.rows.map((r) => r.id),
                  ]),
                ]
                  .map((id) => {
                    const rowA =
                      statsA.rows.find((r) => r.id === id) ||
                      { name: findName(id, apartments), bookings: 0, percent: 0 };
                    const rowB =
                      statsB.rows.find((r) => r.id === id) ||
                      { name: findName(id, apartments), bookings: 0, percent: 0 };
                    const name = rowA.name || rowB.name || findName(id, apartments);
                    return (
                      <tr key={`apt-${id}`}>
                        <td>{name}</td>
                        <td>{rowA.bookings}</td>
                        <td>{rowA.percent.toFixed(0)}%</td>
                        <td>{rowB.bookings}</td>
                        <td>{rowB.percent.toFixed(0)}%</td>
                      </tr>
                    );
                  })}
                <tr style={{ fontWeight: "bold", background: "#f4f4f4" }}>
                  <td>tot bookings</td>
                  <td>{statsA.total}</td>
                  <td>100%</td>
                  <td>{statsB.total}</td>
                  <td>100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <small style={{ color: "#666", display: "block", marginTop: 6 }}>
            Counting rule: a booking is counted if any day overlaps the selected year.
            “Unassigned” shows reservations with no apartment selected.
          </small>
        </div>
      </div>
    </div>
  );

  function findName(id, list) {
    if (id === "unassigned") return "Unassigned";
    const a = (list || []).find((x) => x.id === id);
    return a ? a.name : `Apartment #${id}`;
  }
}
